<template>
  <a-row :gutter="[30, 0]">
    <a-col :span="24">
      <a-card class="filter-card" bordered>
        <div
          class="filter-container"
          :class="{ expanded: showFilters }"
          ref="filterContainer"
        >
          <a-row :gutter="[8, 16]">
            <!-- Main Filters Section -->
            <!-- Device Selector (Single) for Station Analytics -->
            <a-col :span="6">
              <a-select
                id="station-analytics-select-device-filter"
                ref="station_analytics_device_select"
                v-model:value="selectedDevice"
                class="w-100"
                show-search
                placeholder="Select Station"
                :options="deviceOptions"
                :loading="isFetchingDevices"
                :filter-option="filterOption"
                :get-popup-container="getPopupContainer"
                size="small"
                @change="handleDeviceSelect"
              />
            </a-col>

            <!-- Date Range Picker -->
            <a-col :span="6">
              <a-range-picker
                id="analytics-trace-date-range-filter"
                v-model:value="dateRange"
                class="w-100"
                :placeholder="['Start date', 'End date']"
                :get-popup-container="getPopupContainer"
                :disabled-date="disabledDate"
                :show-now="false"
                :show-today="false"
                show-time
                size="small"
              >
                <template #renderExtraFooter>
                  <div class="advance-filter-date-range-footer">
                    <a-typography-text
                      type="success"
                      class="mx-2"
                      style="cursor: pointer; color: #40a9ff"
                      @click="handleSelectDateRange(2)"
                    >
                      One Day
                    </a-typography-text>

                    <a-typography-text
                      type="success"
                      style="cursor: pointer; color: #40a9ff"
                      class=""
                      @click="handleSelectDateRange(1)"
                    >
                      One Week
                    </a-typography-text>

                    <a-typography-text
                      type="success"
                      class="mx-2"
                      style="cursor: pointer; color: #40a9ff"
                      @click="handleSelectDateRange(3)"
                    >
                      One Month
                    </a-typography-text>
                  </div>
                </template>
              </a-range-picker>
            </a-col>

            <!-- Update Button -->
            <a-col :span="3">
              <a-button
                id="analytics-trace-update-btn"
                type="primary"
                block
                :disabled="fetchingTasks"
                :loading="isFetchingCycles"
                size="small"
                @click="getData()"
              >
                <sync-outlined /> Update
              </a-button>
            </a-col>

            <!-- Toggle Advanced Filters Button -->
            <a-col :span="3" :offset="6">
              <div class="d-flex justify-content-end">
                <a-button
                  id="analytics-trace-advance-filter-toggle-btn"
                  type="default"
                  :loading="isFetchingCycles"
                  size="small"
                  @click="toggleAdvanceFilter"
                >
                  {{ showFilters ? 'Hide ' : 'Show ' }} Advanced Filters
                </a-button>
              </div>
            </a-col>
          </a-row>

          <!-- Advanced Filters Section -->
          <div class="advanced-filters mt-5">
            <a-row :gutter="[8, 16]">
              <!-- Task Selector (Multiple) for Station Analytics -->
              <a-col :span="8">
                <a-select
                  id="station-analytics-select-task-filter"
                  ref="station_analytics_task_select"
                  v-model:value="selectedTasks"
                  class="w-100"
                  mode="multiple"
                  show-search
                  placeholder="Select Operation(s)"
                  :options="taskOptions"
                  :loading="isFetchingTasks"
                  :filter-option="filterOption"
                  :get-popup-container="getPopupContainer"
                  :max-tag-count="1"
                  @change="handleTaskSelect"
                  size="small"
                />
              </a-col>

              <!-- Select All Tasks Button -->
              <a-col :span="4">
                <a-button block size="small" @click="handleSelectAllTasks">
                  {{ isAllTasksSelected ? 'Unselect all' : 'Select all' }}
                </a-button>
              </a-col>

              <!-- Select MetaData -->
              <a-col :span="12">
                <a-row :gutter="[8, 16]">
                  <!-- Steps Selector -->
                  <a-col :span="12">
                    <a-select
                      id="analytics-trace-select-meta-data-filter"
                      v-model:value="selectedMetaData"
                      class="w-100"
                      placeholder="Select Meta Data"
                      :options="metaDataOptions"
                      :loading="isFetchingMetaData"
                      :filter-option="filterOption"
                      :get-popup-container="getPopupContainer"
                      :max-tag-count="1"
                      size="small"
                    />
                  </a-col>

                  <a-col :span="12">
                    <a-select
                      id="analytics-trace-select-meta-data-comparison"
                      v-model:value="selectedMetaDataComparison"
                      class="w-100"
                      placeholder="Select Meta Data Comparison Operator"
                      :options="metaDataComparisonOptions"
                      :get-popup-container="getPopupContainer"
                      :max-tag-count="1"
                      size="small"
                    />
                  </a-col>

                  <!-- Select All Steps Button -->
                  <a-col :span="12">
                    <a-input
                      v-model:value="metaDataValue"
                      placeholder="Enter Meta Value"
                      class="w-100"
                      size="small"
                    />
                  </a-col>
                </a-row>
              </a-col>
            </a-row>
          </div>

          <div class="advanced-filters mt-3">
            <a-row :gutter="[8, 16]">
              <!-- First Group -->

              <a-col :span="6">
                <a-row :gutter="[8, 16]">
                  <!-- Cycle Type Selector -->
                  <a-col :span="24">
                    <a-select
                      id="analytics-trace-select-cycle-type-filter"
                      v-model:value="cycleType"
                      class="w-100"
                      placeholder="All Cycles"
                      :options="cycleTypeOptions"
                      :loading="isFetchingDevices"
                      :filter-option="filterOption"
                      :get-popup-container="getPopupContainer"
                      :max-tag-count="1"
                      size="small"
                    />
                  </a-col>
                </a-row>
              </a-col>

              <a-col :span="12" :offset="6">
                <a-row
                  :gutter="[8, 16]"
                  class="d-flex justify-content-end align-items-center"
                >
                  <a-checkbox v-model:checked="excludeTimeoutCycles">
                    Exclude Timeout Cycles
                  </a-checkbox>
                </a-row>
              </a-col>
            </a-row>
          </div>
        </div>
      </a-card>
    </a-col>
  </a-row>
</template>
<script>
import {
  ClockCircleOutlined,
  CloseCircleOutlined,
  DiffOutlined,
  SyncOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import { chartsIndicesForSecondDateRange } from 'src/config/charts-config';
import { dateTimeFormat } from 'src/config/date-format-config.js';
import spaceMixin from 'src/mixins/handleSpace';
import { getSortedDevice } from 'src/utils/device';
import { getSortedTask } from 'src/utils/task';
import { mapActions, mapGetters, mapState } from 'vuex';
import dateHelper from 'src/components/shared/Helpers/dateHelper';
import getTime from 'src/components/shared/Helpers/getTime';
import updateUrl from 'src/components/shared/Helpers/updateUrl';
import TelemetryService from 'src/services/telemetry';

export default {
  components: {
    CloseCircleOutlined,
    ClockCircleOutlined,
    DiffOutlined,
    SyncOutlined,
  },
  mixins: [spaceMixin],
  inject: ['toast'],
  props: {
    showCycleInput: { type: Boolean, required: true, default: false },
    showDurationInput: { type: Boolean, required: true, default: false },
    showStepsFilter: { type: Boolean, required: true, default: false },
    isNoDataAvailable: { type: Boolean, required: true, default: false },
    isAnalytics: { type: Boolean, required: true, default: false },
  },
  emits: ['change', 'updateCards', 'searchDocs', 'changePage'],
  data() {
    return {
      excludeTimeoutCycles: false,
      cycleType: null,
      runId: '',
      selectedModelVersion: null,
      fetchingTasks: false,
      isAllTagsSelected: false,
      selectedTags: [],
      organizationTagOptions: [],
      showFilters: false,
      dateRange: [null, null],
      cycleNatureOptions: [
        { label: 'All Cycles', value: 'all_cycles' },
        { label: 'Good Cycles', value: 'good_cycles' },
        { label: 'Bad Cycles', value: 'bad_cycles' },
      ],
      selectedDeviceModelVersion: [],
      selectedDevice: null, // Single device for Station Analytics
      selectedDeviceForComparison: null, // Single device for comparison
      selectedTasks: [], // Multiple tasks for Station Analytics
      selectedSteps: [],
      selectedStepsForInterventions: [],
      minCycleTime: 0,
      maxCycleTime: 0,
      cyclesFromParams: null,
      durationStartTime: null,
      durationEndTime: null,
      selectedMetaData: null,
      selectedMetaDataComparison: null,
      MetaDataOptions: [],
      metaDataComparisonOptions: [
        { label: 'Lesser than', value: '<' },
        { label: 'Lesser than or Equal to', value: '<=' },
        { label: 'Greater than', value: '>' },
        { label: 'Greater than or Equal to', value: '=>' },
        { label: 'Equals to', value: '=' },
      ],
      metaDataValue: null,
      selectedTaskId: null,
      containerHeight: 'auto',
    };
  },

  computed: {
    ...mapGetters([
      'modelTypes',
      'organizationTags',
      'taskTags',
      'modelVersions',
      'selectedDeviceTasks',
      'taskCreatedAt',
      'isFetchingCycles',
      'organization',
      'isFetchingTasks',
      'allTasks',
      'selectedTask',
      'isFetchingDevices',
      'devices',
      'cycleTypes',
      'selectedCycleType',
      'deviceSerialNumToDisplayNameMap',
      'deviceDisplayNameToSerialNumMap',
      'negativeSteps',
      'optionalSteps',
      'taskProcesses',
      'stepsToIndexMapping',
      'virtualTasks',
      'selectedVirtualTask',
      'showCompareAnalytics',
      'dateRangeForComparison',
      'isFetchingAnalyticsForComparison',
      'isFetchingMetaData',
      'metaData',
    ]),

    taskOptions() {
      return getSortedTask(this.allTasks);
    },

    deviceOptions() {
      const filteredDevices = this.devices?.filter(
        (device) => !!device.Serial_number
      );
      return getSortedDevice(filteredDevices);
    },

    metaDataOptions() {
      return this.metaData?.map((item) => ({
        label: item.key,
        value: item.key,
      }));
    },

    compareDeviceOptions() {
      const filteredDevices = this.devices?.filter(
        (device) => !!device.Serial_number
      );
      return getSortedDevice(filteredDevices);
    },

    cycleTypeOptions() {
      return Object.entries(this.cycleTypes).map(([key, value]) => ({
        value: value,
        label: key,
      }));
    },

    isAllTasksSelected() {
      return this.selectedTasks.length === this.allTasks.length;
    },

    isAllStepsSelected() {
      return (
        this.selectedSteps.length ===
        this.stepFilterOptions?.filter(
          (option) => !this.isNegativeOrOptionalStep(option.value)
        ).length
      );
    },

    isAllStepsSelectedForInterventions() {
      return (
        this.selectedStepsForInterventions.length ===
        this.stepFilterOptions?.filter(
          (option) => !this.isNegativeOrOptionalStep(option.value)
        ).length
      );
    },

    stepFilterOptions() {
      if (!this.taskProcesses.length) return;
      let subSteps = [];
      this.taskProcesses.forEach((process) => {
        const processSubSteps = process.steps.reduce(
          (res, el) => [...res, ...el.substeps],
          []
        );
        subSteps = [...subSteps, ...processSubSteps];
      });
      return subSteps.map((step) => {
        let option = {
          value: this.stepsToIndexMapping[step],
          label: step,
        };
        option['disabled'] = this.isNegativeOrOptionalStep(option.value);
        if (this.negativeSteps.includes(option.value)) {
          option['label'] = option['label'] + '  -  ' + '(negative)';
          option['class'] = 'text-secondary';
        }
        if (this.optionalSteps.includes(option.value)) {
          option['label'] = option['label'] + '  -  ' + '(optional)';
          option['class'] = 'text-secondary';
        }
        return option;
      });
    },
  },

  watch: {
    taskTags: {
      handler(value) {
        this.organizationTagOptions = value.map((tag) => {
          return {
            value: tag.tag,
            label: tag.tag_value,
          };
        });
      },
      deep: true,
    },
    taskCreatedAt(value) {
      if (!value || this.runId) return;
      this.handleSelectDateRange(2);
    },

    modelVersions: {
      handler(value) {
        let modalTypesMapping = {};

        this.modelTypes.forEach((data) => {
          modalTypesMapping[data.id] = data.name;
        });
        this.selectedDeviceModelVersion = value.map((data) => ({
          label:
            `${modalTypesMapping[data.model_type]}-${data.version_number}` ||
            data.version_number,
          value: data.id,
        }));
      },
      deep: true,
    },

    cyclesFromParams(value) {
      if (!value || !Array.isArray(value)) return;
      const data = this.getCardsData();
      this.$emit('updateCards', data);
    },

    async selectedDevice() {
      // Skip these operations for Station Analytics
      // Station Analytics uses handleDeviceSelect instead
      return;
    },

    async selectedTasks() {
      // Skip these operations for Station Analytics
      // These are only needed for Operation Analytics
      return;
    },

    selectedVirtualTask(value) {
      if (value) {
        this.fetchVirtualTaskDetails(value);
      } else {
        this.resetVirtualTaskStates();
      }
    },

    async devices() {
      // Populate filters from URL query parameters for Station Analytics
      await this.populateStationFiltersFromURL();
    },

    taskProcesses() {
      this.setIndexToStepsMapping();
      this.setStepsToIndexMapping();
      this.setStepToAttributesMapping();
    },

    showCompareAnalytics(value) {
      if (!value) {
        const newQueryParam = { ...this.$route.query };
        delete newQueryParam['c_start_date'];
        delete newQueryParam['c_end_date'];
        delete newQueryParam['c_device'];
        this.selectedDeviceForComparison = null;
        this.setDateRangeForComparison([
          dateHelper._getDate(),
          dateHelper._getDate(),
        ]);
        this.$router.replace({
          name: this.$route.name,
          query: newQueryParam,
        });

        const filterContainer = this.$refs.filterContainer;

        if (this.showFilters) {
          filterContainer.style.height = `100px`;
        }
      } else {
        const filterContainer = this.$refs.filterContainer;

        if (this.showFilters) {
          filterContainer.style.height = `225px`;
        }
      }
    },
  },

  mounted() {
    this.$refs.filterContainer.style.height = '25px';
  },

  created() {
    this.clearTaskDetails();
    this.getAllTasks();
    this.getOrgDevices(this.organization);
    this.fetchModelTypes();
    this.fetchOrganizationTags();
    this.emitter.on('getAnalyticsAndTrace', this.getData);
  },

  beforeUnmount() {
    this.setSelectedTask(null);
    this.resetTaskCreatedAt();
    this.dateRange = [null, null];
  },

  methods: {
    ...mapState(['isFetchingMetaData']),
    ...mapActions([
      'fetchModelTypes',
      'getTaskModelVersions',
      'clearTaskDetails',
      'setIsMissedStepsSelected',
      'setIsStepsSelectedForInterventions',
      'fetchOrganizationTags',
      'resetTaskCreatedAt',
      'getAllTasks',
      'fetchTaskDetails',
      'getOrgDevices',
      'setSelectedTask',
      'setSelectedCycleType',
      'setIndexToStepsMapping',
      'setStepsToIndexMapping',
      'setStepToAttributesMapping',
      'fetchVirtualTasks',
      'getTaskDevices',
      'getTagsDevices',
      'fetchVirtualTaskDetails',
      'resetVirtualTaskStates',
      'setSelectedVirtualTask',
      'setShowCompareAnalytics',
      'setDateRangeForComparison',
      'setMetaData',
    ]),

    comparingAnalytics() {
      this.setShowCompareAnalytics(!this.showCompareAnalytics);
    },

    handleSelectDateRange(rangeType) {
      if (rangeType == 1) {
        this.dateRange = [
          dateHelper.subtractDays(new Date(), 7),
          dateHelper._getDate(new Date()),
        ];
      } else if (rangeType == 2) {
        this.dateRange = [
          dateHelper.getTodayStartTime(),
          dateHelper._getDate(new Date()),
        ];
      } else if (rangeType == 3) {
        this.dateRange = [
          dateHelper.subtractMonths(new Date(), 1),
          dateHelper._getDate(new Date()),
        ];
      }
    },

    toggleAdvanceFilter() {
      this.showFilters = !this.showFilters;
      const filterContainer = this.$refs.filterContainer;
      if (this.showFilters) {
        filterContainer.style.height = `${filterContainer.scrollHeight}px`;
      } else {
        filterContainer.style.height = '25px';
      }
    },

    disabledDate(current) {
      // Disable dates before task creation date
      if (current && this.taskCreatedAt) {
        const taskCreatedDate = this.getDateTime(
          dateHelper._getDate(new Date(this.taskCreatedAt))
        );
        if (this.getDateTime(current) < taskCreatedDate) {
          return true;
        }
      }

      // Disable dates after today
      // if (current && this.getDateTime(current) > this.getDateTime(new Date())) {
      //   return true;
      // }

      return false;
    },

    getPopupContainer(trigger) {
      return trigger.parentNode;
    },

    handleTagSelect(val) {
      if (val !== this.selectedTags) this.selectedTags = [];
      this.selectedTags = val;
    },

    async handleDeviceSelect(val) {
      this.selectedDevice = val;
      if (this.$refs.station_analytics_device_select) {
        this.$refs.station_analytics_device_select.blur();
      }

      // Fetch unique tasks for the selected device
      if (val) {
        await this.fetchUniqueTasksForDevice(val);
      }
    },

    async fetchUniqueTasksForDevice(deviceId) {
      try {
        this.fetchingTasks = true;

        // Convert display name to serial number if needed
        // deviceId could be either display name or serial number
        const serialNumber =
          this.deviceDisplayNameToSerialNumMap[deviceId] || deviceId;

        const [error, data] = await TelemetryService.fetchUniqueTasksByDevice({
          device_id: serialNumber,
        });

        if (error || !data) {
          this.toast.error('Failed to fetch tasks for the selected device');
          this.selectedTasks = [];
          return;
        }

        // Auto-select all tasks returned from the API
        // Match the API task IDs with tasks available in allTasks
        if (Array.isArray(data) && data.length > 0) {
          // Filter to only include tasks that exist in allTasks
          console.log('all tasks', this.allTasks);
          const validTaskIds = data
            .map((apiTask) => {
              // Find matching task in allTasks by task ID
              const matchingTask = this.allTasks.find(
                (task) =>
                  task.id === apiTask.task_id ||
                  task.taskName === apiTask.taskName
              );

              return matchingTask ? matchingTask.id : null;
            })
            .filter((id) => id !== null);

          this.selectedTasks = validTaskIds;

          if (validTaskIds.length === 0) {
            this.toast.info('No matching tasks found for this device');
          }
        } else {
          this.selectedTasks = [];
        }
      } catch (error) {
        console.error('Error fetching unique tasks by device:', error);
        this.toast.error('Failed to fetch tasks for the selected device');
        this.selectedTasks = [];
      } finally {
        this.fetchingTasks = false;
      }
    },

    handleTaskSelect(val) {
      this.selectedTasks = val;
      if (this.$refs.station_analytics_task_select) {
        this.$refs.station_analytics_task_select.blur();
      }
    },

    checkDuration() {
      let notValid = false;
      if (this.durationEndTime && this.durationStartTime) {
        notValid =
          parseInt(this.durationEndTime) <= parseInt(this.durationStartTime)
            ? true
            : false;
      }
      return notValid;
    },

    getPayload() {
      if (
        this.dateRange[0] === null &&
        this.dateRange[1] === null &&
        !this.runId
      ) {
        this.handleSelectDateRange(2);
      }
      const data = {
        ...(this.runId && { runId: this.runId }),
        organization: this.organization,
        cycle_type: this.cycleType,
        exclude_timeout_cycles: this.excludeTimeoutCycles,
        model_version: this.selectedModelVersion,
        tasks: JSON.stringify(this.selectedTasks), // Multiple tasks for Station Analytics
        ...(!this.isAnalytics && {
          tags: JSON.stringify(this.selectedTags.map((data) => data)),
        }),
        device: this.selectedDevice, // Single device for Station Analytics
        datetime_start:
          this.dateRange[0] && this.getDateTime(this.dateRange[0]),
        datetime_end: this.dateRange[1] && this.getDateTime(this.dateRange[1]),
        cyclesFromParams: this.cyclesFromParams,
        ...(this.$route.query.stepIndex && {
          step_index: this.$route.query.stepIndex,
        }),
      };

      if (this.selectedTasks.length === 0 && !this.runId) {
        this.toast.info('Task is not selected');
        return false;
      }

      if ((!this.dateRange[0] || !this.dateRange[1]) && !this.runId) {
        this.toast.info('Duration must have both start and end time');
        return false;
      }

      if (
        this.getDateTime(this.dateRange[0]) >
          this.getDateTime(this.dateRange[1]) &&
        !this.runId
      ) {
        this.toast.info('End date should not be before start date.');
        return false;
      }

      if (!this.selectedDevice && !this.runId) {
        this.toast.info('Device is not selected');
        return false;
      }

      if (this.checkDuration() && !this.runId) {
        this.toast.info(
          'In Duration, End time should be greater than start time!'
        );
        return false;
      }

      if (this.durationStartTime) data['start_time'] = this.durationStartTime;
      if (this.durationEndTime) data['end_time'] = this.durationEndTime;
      if (this.minCycleTime) data['min_cycle_time_in_bin'] = this.minCycleTime;
      if (this.maxCycleTime) data['max_cycle_time_in_bin'] = this.maxCycleTime;
      if (this.selectedVirtualTask)
        data['virtualTask'] = this.selectedVirtualTask;
      if (this.selectedSteps.length) {
        data['missed_steps'] = this.getSelectedStepData();
        this.setIsMissedStepsSelected(true);
      } else {
        this.setIsMissedStepsSelected(false);
      }
      if (this.selectedStepsForInterventions.length) {
        data['interventions'] = this.getSelectedStepDataForInterventions();
        this.setIsStepsSelectedForInterventions(true);
      } else {
        this.setIsStepsSelectedForInterventions(false);
      }

      if (this.showCompareAnalytics) {
        this.setStartEndDateForComparison(data);
        if (this.selectedDeviceForComparison) {
          data['c_device'] = this.selectedDeviceForComparison;
        }
      }
      if (this.selectedMetaData && this.metaDataValue) {
        data['meta_value'] = this.metaDataValue;
        data['meta_key'] = this.selectedMetaData;
        if (this.selectedMetaDataComparison) {
          data['meta_comparison_operator'] = this.selectedMetaDataComparison;
        }
      }
      return data;
    },

    setStartEndDateForComparison(data) {
      const [startDate, endDate] = this.dateRangeForComparison;
      const [_startDate, _endDate] = [
        this.getDateTime(startDate),
        this.getDateTime(endDate),
      ];
      data['c_start_date'] = _startDate;
      data['c_end_date'] = _endDate;
    },

    getData(isTraceOnly = false, callBack = () => {}) {
      const data = this.getPayload();
      if (!data) return;
      !this.isKaizenAdvisor && updateUrl.apply(this, [data]);
      if (!isTraceOnly) this.$emit('changePage', 1);
      this.$emit('change', data, isTraceOnly, callBack);
    },

    getDataForComparison() {
      const data = this.getPayload();
      if (!data) return;
      this.setStartEndDateForComparison(data);
      !this.isKaizenAdvisor && updateUrl.apply(this, [data]);
      data['datetime_start'] = data['c_start_date'];
      data['datetime_end'] = data['c_end_date'];
      data['device'] = data['c_device'];
      data['isComparing'] = true;
      this.$emit('change', data);
      return data;
    },

    getDateTime(date) {
      return `${dayjs(date).format(dateTimeFormat)}`;
    },

    redirectToPage(name, query = {}, chartIndex) {
      let key = 'dateRange';
      if (this.showCompareAnalytics) {
        key = chartsIndicesForSecondDateRange.includes(chartIndex)
          ? 'dateRangeForComparison'
          : key;
      }
      const [startDate, endDate] = this[key];

      query = {
        ...query,
        tasks: JSON.stringify(this.selectedTasks),
        device: this.selectedDevice,
        startTime: this.getDateTime(startDate),
        endTime: this.getDateTime(endDate),
        exclude_timeout_cycles: this.excludeTimeoutCycles,
        ...(this.cycleType && {
          cycleType: this.cycleType,
        }),
        ...(this.selectedModelVersion && {
          modelVersion: this.selectedModelVersion,
        }),
      };

      let { href } = this.$router.resolve({ name, query });
      window.open(href, '_blank');
    },

    getSumOfCycles() {
      return this.cyclesFromParams.reduce((res, el) => res + el);
    },

    getAverageCycleTime(sumOfCyclesTime, cyclesCount) {
      const averageCycleTime = sumOfCyclesTime / cyclesCount;
      return getTime(averageCycleTime * 1000);
    },

    getSelectedStepData() {
      const paramsSteps = this.selectedSteps.map((ix) => ix);
      return JSON.stringify(paramsSteps);
    },

    getSelectedStepDataForInterventions() {
      const paramsSteps = this.selectedStepsForInterventions.map((ix) => ix);
      return JSON.stringify(paramsSteps);
    },

    isNegativeOrOptionalStep(index) {
      return (
        this.negativeSteps?.includes(index) ||
        this.optionalSteps?.includes(index)
      );
    },

    getCardsData() {
      const sum_of_cycles_time = this.getSumOfCycles();
      const number_of_cycles = this.cyclesFromParams.length;
      const total_cycles_time = getTime(sum_of_cycles_time * 1000);
      const average_cycle_time = this.getAverageCycleTime(
        sum_of_cycles_time,
        number_of_cycles
      );

      return {
        number_of_cycles,
        average_cycle_time,
        total_cycles_time,
        total_inference_time: total_cycles_time,
      };
    },

    handleSelectAllTasks() {
      if (this.isAllTasksSelected) {
        this.selectedTasks = [];
        return;
      }
      this.selectedTasks = this.allTasks.map((task) => task.id);
    },

    handleSelectAllTags() {
      if (this.isAllTagsSelected) {
        this.selectedTags = [];
        this.isAllTagsSelected = false;
        return;
      }
      this.selectedTags = this.organizationTagOptions?.map(
        (step) => step.value
      );
      this.isAllTagsSelected = true;
    },

    handleSelectAllSteps() {
      if (this.isAllStepsSelected) {
        this.selectedSteps = [];
        return;
      }
      this.selectedSteps = this.stepFilterOptions
        ?.map((step) => step.value)
        .filter((index) => !this.isNegativeOrOptionalStep(index));
    },

    handleSelectAllStepsForInterventions() {
      if (this.isAllStepsSelectedForInterventions) {
        this.selectedStepsForInterventions = [];
        return;
      }
      this.selectedStepsForInterventions = this.stepFilterOptions
        ?.map((step) => step.value)
        .filter((index) => !this.isNegativeOrOptionalStep(index));
    },

    handleVirtualTaskChange(e) {
      const { value } = e.target;
      this.setSelectedVirtualTask(value);
    },

    async populateStationFiltersFromURL() {
      const {
        device,
        tasks,
        datetime_start,
        datetime_end,
        cycle_type,
        exclude_timeout_cycles,
        meta_key,
        meta_value,
        meta_comparison_operator,
      } = this.$route.query;

      // Only populate if we have the required parameters
      if (!device || !datetime_start || !datetime_end) {
        return;
      }

      // Populate device (single device for Station Analytics)
      // The device in query params is the display name
      this.selectedDevice = device;

      // Populate tasks (multiple tasks for Station Analytics)
      if (tasks) {
        try {
          const taskIds = JSON.parse(tasks);
          if (Array.isArray(taskIds)) {
            this.selectedTasks = taskIds;
          }
        } catch (error) {
          console.error('Error parsing tasks from URL:', error);
        }
      }

      // Populate date range
      const convertDateToISOFormat = (date) => {
        const hoursToAdd =
          new Date().toString().split('GMT').pop().split(' ')[0] / 100;
        return dayjs(
          new Date(date.replace(/-/g, '/')).toISOString(),
          dateTimeFormat
        ).add(hoursToAdd, 'hour');
      };

      this.dateRange = [
        convertDateToISOFormat(datetime_start),
        convertDateToISOFormat(datetime_end),
      ];

      // Populate cycle type
      if (cycle_type) {
        this.cycleType = cycle_type;
      }

      // Populate exclude_timeout_cycles
      if (exclude_timeout_cycles !== undefined) {
        this.excludeTimeoutCycles = exclude_timeout_cycles === 'true';
      }

      // Populate metadata filters
      if (meta_key) {
        this.selectedMetaData = meta_key;
      }
      if (meta_value) {
        this.metaDataValue = meta_value;
      }
      if (meta_comparison_operator) {
        this.selectedMetaDataComparison = meta_comparison_operator;
      }

      // Automatically fetch data after populating filters
      this.getData();
    },
  },
};
</script>
<style scoped>
.advance-filter-date-range-footer {
  display: flex;
  align-items: center;
}

.hidden {
  visibility: hidden;
}
.filter-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.w-100 {
  width: 100%;
}

.d-flex {
  display: flex;
}

.justify-content-end {
  justify-content: flex-end;
}

.filter-container {
  height: 25px;
  transition: height 0.5s ease-in-out, opacity 0.3s ease-in-out;
  opacity: 1;
}

.filter-container.expanded {
  opacity: 1;
}

.advanced-filters {
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.filter-container.expanded .advanced-filters {
  visibility: visible;
  opacity: 1;
}

.dependent-filters {
  transition: opacity 1s ease-in;
}

@keyframes fadeExpand {
  0% {
    height: 25px;
    opacity: 0;
  }
  100% {
    height: auto;
    opacity: 1;
  }
}
</style>
